import PropTypes from 'prop-types'
import { useEffect, useRef } from 'react'
import styled from 'styled-components'

import { useIsMounted } from 'shared/hooks/useIsMounted'
import { useNoty } from 'shared/hooks/useNoty'

import Portal from 'hsds/components/portal'
import SimpleModal, { Confirmation } from 'hsds/components/simple-modal'
import { ConfirmationWrapperUI } from 'hsds/components/simple-modal/SimpleModal.layouts.styles'

const SimpleModalUI = styled(SimpleModal)`
  ${ConfirmationWrapperUI} {
    ${({ $modalWidth }) => $modalWidth && `width: ${$modalWidth}px;`}
    ${({ $modalHeight }) => $modalHeight && `height: ${$modalHeight}px;`}
  }
`

const NotyConfirm = ({
  danger,
  isVisible,
  onConfirm,
  onClose,
  primaryButtonText,
  secondaryButtonText,
  title,
  body,
  modalWidth,
  modalHeight,
  fixedPosition = false,
  closeOnConfirm = true,
}) => {
  const { hideConfirmNoty } = useNoty()
  const isMounted = useIsMounted()
  const cancelButtonRef = useRef()

  // For "danger" modals, we'll automatically focus the cancel (i.e. safe) action
  // to help prevent accidents
  useEffect(() => {
    if (!isVisible || !danger) return

    setTimeout(() => {
      if (cancelButtonRef.current) {
        cancelButtonRef.current.focus()
      }
    }, 50)
  }, [isVisible, danger])

  const handleClose = event => {
    // There's some weird hs-app stuff around modal management that,
    // when you hit the esc key specifically, results in an unintentional click event.
    // this guard helps us to ignore that event, while letting through other clicks
    // More context: https://helpscout.slack.com/archives/C013J9TH17X/p1673471395959489?thread_ts=1673469580.701399&cid=C013J9TH17X
    const shouldIgnoreEvent =
      !event?.target?.classList?.contains('SimpleModal__Overlay') ||
      !event?.target?.closest('.SimpleModal__Overlay')

    if (event?.type === 'click' && shouldIgnoreEvent) {
      return
    }

    onClose && onClose()
    isMounted() && hideConfirmNoty()
  }

  return (
    <Portal className="hsds-react-hs-app">
      <SimpleModalUI
        aria-label={title}
        closeOnClickOutside="modal"
        onClose={handleClose}
        role="alertdialog"
        show={isVisible}
        unsetDimensions={true}
        withCloseButton={false}
        $modalWidth={modalWidth}
        $modalHeight={modalHeight}
        fullscreen={fixedPosition}
        focusModalOnShow={!danger}
      >
        <Confirmation
          body={body}
          cancelButtonText={secondaryButtonText || 'Cancel'}
          confirmButtonText={primaryButtonText || 'OK'}
          cancelButtonProps={{ ref: cancelButtonRef }}
          danger={danger}
          heading={title}
          onCancel={() => {
            // we don't want to pass through the event object because we want this to always be called
            handleClose()
          }}
          onConfirm={() => {
            onConfirm && onConfirm()
            closeOnConfirm && handleClose()
          }}
        />
      </SimpleModalUI>
    </Portal>
  )
}

NotyConfirm.propTypes = {
  danger: PropTypes.bool,
  isVisible: PropTypes.bool,
  onConfirm: PropTypes.func,
  onClose: PropTypes.func,
  primaryButtonText: PropTypes.string,
  secondaryButtonText: PropTypes.string,
  title: PropTypes.string,
  body: PropTypes.any,
  modalWidth: PropTypes.number,
  modalHeight: PropTypes.number,
  closeOnConfirm: PropTypes.bool,
  fixedPosition: PropTypes.bool,
}

export default NotyConfirm
